import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Autocomplete,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Snackbar
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Sync,
  CalendarToday,
  AccessTime,
  CheckCircle,
  Warning,
  Business,
  Assignment,
  PlayArrow,
  Check,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Days of the week in Dutch
const DAYS = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];

// Get the Monday of the current week
function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
}

// Format date as YYYY-MM-DD
function formatDate(date) {
  return date.toISOString().split('T')[0];
}

// Format date for display (DD-MM)
function formatDateShort(dateStr) {
  const date = new Date(dateStr);
  return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
}

export default function UrenSchrijven() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username || '';

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Week selection
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [weekDays, setWeekDays] = useState([]);

  // Tasks from Airtable
  const [tasksByDay, setTasksByDay] = useState({});
  const [selectedTasks, setSelectedTasks] = useState([]);

  // Gripp data
  const [grippProjects, setGrippProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectLines, setProjectLines] = useState([]);
  const [taskMappings, setTaskMappings] = useState({});

  // Internal project (9252)
  const INTERNAL_PROJECT_ID = 9252;

  // Calculate week days when weekStart changes
  useEffect(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);
      days.push(formatDate(day));
    }
    setWeekDays(days);
  }, [weekStart]);

  // Fetch tasks when week changes
  useEffect(() => {
    if (userEmail && weekDays.length === 7) {
      fetchTasks();
    }
  }, [userEmail, weekDays]);

  // Fetch tasks from Airtable
  const fetchTasks = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE}/Api/airtable/tasks/week?email=${encodeURIComponent(userEmail)}&weekStart=${formatDate(weekStart)}`
      );

      if (!response.ok) throw new Error('Kon taken niet ophalen');

      const data = await response.json();
      setTasksByDay(data.tasksByDay || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Gripp projects
  const fetchGrippProjects = async (search = '') => {
    try {
      const url = search
        ? `${API_BASE}/Api/gripp/projects?search=${encodeURIComponent(search)}`
        : `${API_BASE}/Api/gripp/projects?limit=50`;

      const response = await fetch(url);
      if (!response.ok) throw new Error('Kon Gripp projecten niet ophalen');

      const data = await response.json();
      setGrippProjects(data.projects || []);
    } catch (err) {
      console.error('Error fetching Gripp projects:', err);
    }
  };

  // Fetch project lines when project is selected
  const fetchProjectLines = async (projectId) => {
    if (!projectId) return;

    try {
      const response = await fetch(`${API_BASE}/Api/gripp/projects/${projectId}/lines`);
      if (!response.ok) throw new Error('Kon offerteregels niet ophalen');

      const data = await response.json();
      setProjectLines(data.lines || []);
    } catch (err) {
      console.error('Error fetching project lines:', err);
    }
  };

  // Handle project selection
  const handleProjectSelect = async (project) => {
    setSelectedProject(project);
    if (project) {
      await fetchProjectLines(project.id);
    } else {
      setProjectLines([]);
    }
  };

  // Navigate weeks
  const navigateWeek = (direction) => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setWeekStart(newDate);
  };

  // Toggle task selection
  const toggleTaskSelection = (task) => {
    setSelectedTasks(prev => {
      const exists = prev.find(t => t.id === task.id);
      if (exists) {
        return prev.filter(t => t.id !== task.id);
      } else {
        return [...prev, task];
      }
    });
  };

  // Select all tasks for a day
  const selectAllDay = (dayTasks) => {
    const dayTaskIds = dayTasks.map(t => t.id);
    const allSelected = dayTasks.every(t => selectedTasks.find(st => st.id === t.id));

    if (allSelected) {
      setSelectedTasks(prev => prev.filter(t => !dayTaskIds.includes(t.id)));
    } else {
      setSelectedTasks(prev => {
        const existing = prev.filter(t => !dayTaskIds.includes(t.id));
        return [...existing, ...dayTasks];
      });
    }
  };

  // Update task mapping
  const updateMapping = (taskType, projectLineId) => {
    setTaskMappings(prev => ({
      ...prev,
      [taskType]: projectLineId
    }));
  };

  // Get unique task types from selected tasks
  const getUniqueTaskTypes = () => {
    const types = new Set(selectedTasks.map(t => t.type).filter(Boolean));
    return Array.from(types);
  };

  // Sync hours to Gripp
  const syncHours = async () => {
    if (selectedTasks.length === 0) {
      setError('Selecteer eerst taken om te synchroniseren');
      return;
    }

    if (!selectedProject) {
      setError('Selecteer eerst een Gripp project');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/Api/uren/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tasks: selectedTasks,
          grippProjectId: selectedProject.id,
          mappings: taskMappings
        })
      });

      if (!response.ok) throw new Error('Synchronisatie mislukt');

      const data = await response.json();

      if (data.success) {
        setSuccess(`${data.written} uren succesvol geschreven naar Gripp!`);
        setSelectedTasks([]);
        setActiveStep(0);
      } else {
        setError(`${data.written} geschreven, ${data.failed} mislukt, ${data.unmapped} niet gekoppeld`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const getTotalHours = () => {
    return selectedTasks.reduce((sum, t) => sum + (t.hours || 0), 0);
  };

  const getTotalByDay = (dayStr) => {
    const dayTasks = tasksByDay[dayStr] || [];
    return dayTasks.reduce((sum, t) => sum + (t.hours || 0), 0);
  };

  // Steps
  const steps = ['Taken selecteren', 'Project kiezen', 'Mapping & Bevestigen'];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
              Uren Schrijven naar Gripp
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
              Synchroniseer je Airtable taken met Gripp uren registratie
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Terug
          </Button>
        </Box>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        message={success}
      />

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 0: Task Selection */}
      {activeStep === 0 && (
        <Paper sx={{ p: 3 }}>
          {/* Week Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <IconButton onClick={() => navigateWeek(-1)}>
              <ArrowBack />
            </IconButton>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6">
                <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
                Week {formatDateShort(weekDays[0])} - {formatDateShort(weekDays[6])}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {weekStart.getFullYear()}
              </Typography>
            </Box>
            <IconButton onClick={() => navigateWeek(1)}>
              <ArrowForward />
            </IconButton>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {/* Tasks Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell padding="checkbox" />
                  <TableCell><strong>Dag</strong></TableCell>
                  <TableCell><strong>Project</strong></TableCell>
                  <TableCell><strong>Taak</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell align="right"><strong>Uren</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {weekDays.map((dayStr, dayIndex) => {
                  const dayTasks = tasksByDay[dayStr] || [];
                  const dayTotal = getTotalByDay(dayStr);
                  const isWeekend = dayIndex >= 5;

                  return (
                    <React.Fragment key={dayStr}>
                      {/* Day header row */}
                      <TableRow sx={{ bgcolor: isWeekend ? '#fff3e0' : '#e3f2fd' }}>
                        <TableCell padding="checkbox">
                          {dayTasks.length > 0 && (
                            <Checkbox
                              indeterminate={
                                dayTasks.some(t => selectedTasks.find(st => st.id === t.id)) &&
                                !dayTasks.every(t => selectedTasks.find(st => st.id === t.id))
                              }
                              checked={dayTasks.every(t => selectedTasks.find(st => st.id === t.id))}
                              onChange={() => selectAllDay(dayTasks)}
                            />
                          )}
                        </TableCell>
                        <TableCell colSpan={4}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {DAYS[dayIndex]} ({formatDateShort(dayStr)})
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${dayTotal.toFixed(1)}u`}
                            size="small"
                            color={dayTotal > 0 ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell />
                      </TableRow>

                      {/* Task rows */}
                      {dayTasks.length === 0 ? (
                        <TableRow>
                          <TableCell />
                          <TableCell colSpan={6} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            Geen taken gepland
                          </TableCell>
                        </TableRow>
                      ) : (
                        dayTasks.map(task => {
                          const isSelected = selectedTasks.find(t => t.id === task.id);

                          return (
                            <TableRow
                              key={task.id}
                              hover
                              selected={!!isSelected}
                              onClick={() => toggleTaskSelection(task)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox checked={!!isSelected} />
                              </TableCell>
                              <TableCell />
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {task.projectNumber || task.project}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {task.projectName}
                                </Typography>
                              </TableCell>
                              <TableCell>{task.taskName}</TableCell>
                              <TableCell>
                                <Chip
                                  label={task.type || 'Onbekend'}
                                  size="small"
                                  variant="outlined"
                                  color={task.isMeeting ? 'secondary' : 'default'}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <strong>{task.hours?.toFixed(1) || '0'}u</strong>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={task.status || '-'}
                                  size="small"
                                  color={task.status === 'Afgerond' ? 'success' : 'default'}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Selection Summary */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body1">
                <strong>{selectedTasks.length}</strong> taken geselecteerd
                {' • '}
                <strong>{getTotalHours().toFixed(1)}</strong> uur totaal
              </Typography>
            </Box>
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              disabled={selectedTasks.length === 0}
              onClick={() => {
                fetchGrippProjects();
                setActiveStep(1);
              }}
            >
              Volgende: Project Kiezen
            </Button>
          </Box>
        </Paper>
      )}

      {/* Step 1: Project Selection */}
      {activeStep === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
            Selecteer Gripp Project
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Autocomplete
              options={grippProjects}
              getOptionLabel={(option) => `${option.number || ''} - ${option.name}`}
              value={selectedProject}
              onChange={(_, value) => handleProjectSelect(value)}
              onInputChange={(_, value) => {
                if (value.length >= 2) fetchGrippProjects(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Zoek project (nummer of naam)"
                  placeholder="Typ om te zoeken..."
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {option.number} - {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.company?.name || 'Geen relatie'}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Box>

          {/* Quick select internal project */}
          <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Snel selecteren
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const internalProject = { id: INTERNAL_PROJECT_ID, number: '9252', name: 'Algemene interne uren' };
                  handleProjectSelect(internalProject);
                }}
                sx={{ mr: 1 }}
              >
                9252 - Interne uren (2026)
              </Button>
            </CardContent>
          </Card>

          {/* Project Lines Preview */}
          {selectedProject && projectLines.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Beschikbare offerteregels ({projectLines.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {projectLines.slice(0, 10).map(line => (
                  <Chip
                    key={line.id}
                    label={line.product?.name || line.additionalsubject || `Regel ${line.id}`}
                    size="small"
                    variant="outlined"
                  />
                ))}
                {projectLines.length > 10 && (
                  <Chip label={`+${projectLines.length - 10} meer`} size="small" />
                )}
              </Box>
            </Box>
          )}

          {/* Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button startIcon={<ArrowBack />} onClick={() => setActiveStep(0)}>
              Terug
            </Button>
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              disabled={!selectedProject}
              onClick={() => setActiveStep(2)}
            >
              Volgende: Mapping
            </Button>
          </Box>
        </Paper>
      )}

      {/* Step 2: Mapping & Confirm */}
      {activeStep === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
            Koppel Taaktypes aan Offerteregels
          </Typography>

          {/* Mapping Table */}
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Taaktype</strong></TableCell>
                  <TableCell><strong>Aantal taken</strong></TableCell>
                  <TableCell><strong>Totaal uren</strong></TableCell>
                  <TableCell><strong>Koppelen aan offerteline</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getUniqueTaskTypes().map(type => {
                  const typeTasks = selectedTasks.filter(t => t.type === type);
                  const typeHours = typeTasks.reduce((sum, t) => sum + (t.hours || 0), 0);

                  return (
                    <TableRow key={type}>
                      <TableCell>
                        <Chip label={type} size="small" />
                      </TableCell>
                      <TableCell>{typeTasks.length}</TableCell>
                      <TableCell><strong>{typeHours.toFixed(1)}u</strong></TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 400 }}>
                          <Select
                            value={taskMappings[type] || ''}
                            onChange={(e) => updateMapping(type, e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>Auto-match (op naam)</em>
                            </MenuItem>
                            {projectLines
                              .filter(line => !line.hidefortimewriting) // Filter out lines hidden for time writing
                              .map(line => {
                                // Use searchname which contains the best description
                                // Format: "Groep Preproductie (Fixed)" or product name + additionalsubject
                                const searchName = line.searchname || '';
                                const additionalSubject = line.additionalsubject || '';
                                const productName = line.product?.searchname || '';
                                
                                // Prefer searchname, otherwise build from parts
                                let displayName = searchName || additionalSubject || productName || `Regel ${line.id}`;
                                
                                return (
                                  <MenuItem key={line.id} value={line.id}>
                                    {displayName}
                                  </MenuItem>
                                );
                              })}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 3 }} />

          {/* Summary */}
          <Card sx={{ mb: 3, bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                <CheckCircle sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                Samenvatting
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Geselecteerde taken</Typography>
                  <Typography variant="h5">{selectedTasks.length}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Totaal uren</Typography>
                  <Typography variant="h5">{getTotalHours().toFixed(1)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Gripp project</Typography>
                  <Typography variant="h6">{selectedProject?.number}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button startIcon={<ArrowBack />} onClick={() => setActiveStep(1)}>
              Terug
            </Button>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Sync />}
              disabled={loading}
              onClick={syncHours}
            >
              {loading ? 'Bezig met synchroniseren...' : 'Synchroniseer naar Gripp'}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

